<script lang="ts">
  import { onMount } from "svelte";

  let containerEl: HTMLDivElement | undefined = $state();
  let canvasEl: HTMLCanvasElement | undefined = $state();

  const chars = ".:+*#@%&=~^!?|/\\<>[]{}()";
  const cellWidth = 9;
  const cellHeight = 13;

  let gridData: { char: string; nextChange: number; opacity: number }[] = [];
  let cols = 0;
  let rows = 0;

  // falling highlights state
  let highlights: { col: number; row: number; speed: number; length: number }[] = [];
  let nextSpawn = 0;

  // seeded random for consistent column heights
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed * 12.9898) * 43758.5453;
    return x - Math.floor(x);
  };

  // get CSS custom property value
  const getCssVar = (name: string, fallback: string) =>
    containerEl
      ? getComputedStyle(containerEl).getPropertyValue(name).trim() || fallback
      : fallback;

  // curtain opacity calculation
  const getOpacity = (x: number, y: number): number => {
    const colRandom = seededRandom(Math.floor(x * 80));
    const height = 0.15 + x * 0.4 + colRandom * 0.25 + Math.sin(x * Math.PI * 8) * 0.02;
    if (y > height) return 0;
    const fade = Math.pow(1 - y / height, 1.2 + colRandom * 0.5) * 0.7;
    return fade > 0.08 ? fade : 0;
  };

  function initGrid(width: number, height: number) {
    cols = Math.ceil(width / cellWidth);
    rows = Math.ceil(height / cellHeight);
    gridData = Array.from({ length: rows * cols }, (_, i) => ({
      char: chars[Math.floor(Math.random() * chars.length)],
      nextChange: Math.random() * 5000,
      opacity: getOpacity((i % cols) / cols, Math.floor(i / cols) / rows),
    }));
  }

  function draw(ctx: CanvasRenderingContext2D) {
    const dpr = devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width / dpr, ctx.canvas.height / dpr);
    ctx.font = "10px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const baseColor = getCssVar("--ascii-color", "rgba(200,200,195,0.35)");
    const hlColor = getCssVar("--highlight-color", "rgba(65,55,255,1)");

    // build highlight map
    const hlMap = new Map<string, number>();
    for (const h of highlights) {
      for (let i = 0; i < h.length; i++) {
        const r = Math.floor(h.row) - i;
        if (r >= 0 && r < rows) {
          const fade = 1 - (i / h.length) * 0.8;
          [h.col, h.col + 1].forEach((c) => {
            if (c >= 0 && c < cols) {
              const key = `${c},${r}`;
              hlMap.set(key, Math.max(hlMap.get(key) || 0, fade));
            }
          });
        }
      }
    }

    // draw grid
    gridData.forEach((item, i) => {
      if (item.opacity > 0.03) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const hl = hlMap.get(`${col},${row}`);
        ctx.fillStyle = hl ? hlColor : baseColor;
        ctx.globalAlpha = hl ? item.opacity * hl : item.opacity;
        ctx.fillText(item.char, col * cellWidth + cellWidth / 2, row * cellHeight + cellHeight / 2);
      }
    });
    ctx.globalAlpha = 1;
  }

  let animationFrame: number;
  let lastTime = 0;

  // check for reduced motion preference
  const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function animate(time: number) {
    const delta = time - lastTime;
    lastTime = time;

    // skip animations if reduced motion is preferred (show static grid)
    if (prefersReducedMotion()) {
      if (highlights.length > 0) {
        highlights = [];
        if (canvasEl) {
          const ctx = canvasEl.getContext("2d");
          if (ctx) draw(ctx);
        }
      }
      animationFrame = requestAnimationFrame(animate);
      return;
    }

    // update characters
    gridData.forEach((item) => {
      if (item.opacity > 0.05 && (item.nextChange -= delta) <= 0) {
        item.char = chars[Math.floor(Math.random() * chars.length)];
        item.nextChange = 1000 + Math.random() * 4000;
      }
    });

    // spawn highlights
    if ((nextSpawn -= delta) <= 0 && cols > 2) {
      highlights.push({
        col: Math.floor(Math.random() * (cols - 1)),
        row: 0,
        speed: 8 + Math.random() * 12,
        length: 4 + Math.floor(Math.random() * 6),
      });
      nextSpawn = 800 + Math.random() * 1500;
    }

    // update & cleanup highlights
    highlights = highlights.filter((h) => {
      h.row += h.speed * (delta / 1000);
      return h.row - h.length <= rows;
    });

    // draw
    if (canvasEl) {
      const ctx = canvasEl.getContext("2d");
      if (ctx) draw(ctx);
    }

    animationFrame = requestAnimationFrame(animate);
  }

  function resize() {
    if (!containerEl || !canvasEl) return;
    const dpr = devicePixelRatio || 1;
    const { clientWidth: w, clientHeight: h } = containerEl;
    canvasEl.width = w * dpr;
    canvasEl.height = h * dpr;
    canvasEl.style.width = `${w}px`;
    canvasEl.style.height = `${h}px`;
    initGrid(w, h);
    const ctx = canvasEl.getContext("2d");
    if (ctx) draw(ctx);
  }

  // redraw on theme changee
  $effect(() => {
    if (!containerEl || !canvasEl) return;
    const redraw = () => canvasEl?.getContext("2d") && draw(canvasEl.getContext("2d")!);
    const observer = new MutationObserver(redraw);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  });

  onMount(() => {
    resize();
    animationFrame = requestAnimationFrame(animate);
    const resizeObserver = new ResizeObserver(resize);
    if (containerEl) resizeObserver.observe(containerEl);
    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  });
</script>

<div
  bind:this={containerEl}
  class="pointer-events-none absolute inset-0 overflow-hidden [--ascii-color:var(--color-mono-500)] [--highlight-color:var(--color-pri-800)] dark:[--ascii-color:var(--color-mono-500)]"
  role="presentation"
>
  <canvas bind:this={canvasEl} class="pointer-events-none absolute inset-0" aria-hidden="true"
  ></canvas>
</div>
